import { useRef, useState, type RefObject } from 'react';
import AnchoredPop from '../components/AnchoredPop.tsx';
import { useDismissMenu } from '../lib/useDismissMenu.ts';
import { useMediaQuery, PHONE_QUERY } from '../lib/useMediaQuery.ts';
import Sheet from '@shared-client/Sheet.tsx';
import type { DeckSection, SlideEntry } from '../../shared/deck.ts';
import { ChevronDown, ChevronRight } from '../lib/icons.tsx';

/**
 * Per-tile context menu (kebab or right-click), deliberately lean — eight
 * verbs: add slide after / rename / duplicate / change template /
 * move to section / hide / save to library / delete. Reordering is drag (multi
 * via the checkboxes) or Alt+↑/↓, and split/merge are toolbar verbs in the
 * All-slides overview. Move-to-section stays because a drag can't reach an
 * empty section. Multi-instance slides share one file + manifest row, so the
 * copy spells out that delete affects every instance.
 *
 * ADD-SLIDE CAME BACK (2026-08-19). It was removed on the reasoning that the
 * between-tile insert dot is a better surface for it — truer of where a slide
 * goes than of how people reach for it: the dot is a hover-revealed 9px target
 * between two tiles, so "add one after this" meant aiming at a gap rather than
 * naming the slide you are already looking at (Jake). The dot stays; it is the
 * precise instrument. This is the obvious one, and it always inserts AFTER the
 * slide whose menu is open, which is what the dot under that slide does too —
 * one behaviour, two ways in.
 */
type SlideContextMenuProps = {
  slide: SlideEntry;
  /** The kebab this menu hangs off. The menu is portalled out of the filmstrip
   *  (which clips on both axes), so it needs the trigger's rect to place
   *  itself — see AnchoredPop. Right-click opens the same menu against the same
   *  kebab rather than against the pointer, so both routes land identically. */
  anchor: RefObject<HTMLElement | null>;
  /** Move targets; the slide's own section is filtered out below. */
  sections?: DeckSection[];
  /** > 1 when this slide is a multi:N block — the copy then spells out that
   *  delete affects every instance, since they share one file. */
  multiCount?: number;
  /** Insert a new slide directly AFTER this one, in this slide's own section —
   *  the same call the between-tile insert dot makes. Null hides the item. */
  onAddAfter?: (() => void) | null;
  /** Open the rename-title dialog. */
  onRename?: (() => void) | null;
  onDuplicate: () => void;
  onMoveToSection: (sectionKey: string) => void;
  /** Re-flow this slide's content into a new base template (opens the
   *  chooser). Null hides the item. */
  onChangeTemplate?: (() => void) | null;
  /** Park the slide: kept in the deck, left out when published. */
  onToggleHidden?: (() => void) | null;
  /** Copy this slide into a shelf ('library'); null on bank decks, which are
   *  the shelf. */
  onSaveToBank?: ((bank: string) => void) | null;
  onDelete: () => void;
  onClose: () => void;
  busy?: boolean;
};

export default function SlideContextMenu({
  slide,
  anchor,
  sections,
  multiCount,
  onAddAfter,
  onRename,
  onDuplicate,
  onMoveToSection,
  onChangeTemplate,
  onToggleHidden,
  onSaveToBank,
  onDelete,
  onClose,
  busy,
}: SlideContextMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const isPhone = useMediaQuery(PHONE_QUERY);

  // On a phone the Sheet owns dismissal through its own backdrop and Escape;
  // running this as well gave the close path two owners, and the hook already
  // takes an `enabled` flag for exactly this.
  useDismissMenu(rootRef, onClose, !isPhone);

  const moveTargets = (sections || []).filter((s) => s.key !== slide.section);

  /**
   * The seven verbs, described once. Both shells render from this list, which
   * is the point: an anchored popover on a desktop and a bottom sheet on a
   * phone were otherwise going to be two menus that drift.
   *
   * `run` closes first, then acts — the order the inline handlers used, and it
   * matters for the ones that open another dialog.
   */
  const run = (fn: () => void) => () => {
    onClose();
    fn();
  };
  type Item = {
    key: string;
    label: string;
    danger?: boolean;
    disabled?: boolean;
    title?: string;
    onSelect: () => void;
  };
  /** One descriptor as a sheet row. Declared here so both groups render it
   *  identically without repeating the markup twice more. */
  const SheetItem = ({ item, busy: isBusy }: { item: Item; busy?: boolean }) => (
    <button
      type="button"
      className={'app-sheet__row' + (item.danger ? ' app-sheet__row--danger' : '')}
      disabled={isBusy || item.disabled}
      title={item.title}
      onClick={item.onSelect}
    >
      <span className="app-sheet__row-label">{item.label}</span>
    </button>
  );
  // Two groups, split where Move-to-section sits, rather than one list the
  // shells index into: rename and template are both conditional, so any
  // fixed slice point silently moves when a caller omits one.
  const itemsBeforeMove: Item[] = [
    // First, because it is the one people come here for most and the one that
    // does not depend on reading the rest of the list.
    ...(onAddAfter ? [{ key: 'add-after', label: 'Add slide after', onSelect: run(onAddAfter) }] : []),
    ...(onRename ? [{ key: 'rename', label: 'Rename slide…', onSelect: run(onRename) }] : []),
    { key: 'duplicate', label: 'Duplicate slide', onSelect: run(onDuplicate) },
    ...(onChangeTemplate ? [{ key: 'template', label: 'Change template…', onSelect: run(onChangeTemplate) }] : []),
  ];
  const itemsAfterMove: Item[] = [
    ...(onToggleHidden
      ? [
          {
            key: 'hidden',
            label: slide.hidden ? 'Show slide' : 'Hide slide',
            title: slide.hidden
              ? 'Include this slide when the deck is published'
              : 'Keep the slide in the deck but leave it out when published',
            onSelect: run(onToggleHidden),
          },
        ]
      : []),
    ...(onSaveToBank ? [{ key: 'bank', label: 'Save to library', onSelect: run(() => onSaveToBank('library')) }] : []),
    {
      key: 'delete',
      label: (multiCount ?? 1) > 1 ? `Delete all ${multiCount} instances…` : 'Delete slide…',
      danger: true,
      onSelect: run(onDelete),
    },
  ];

  // Move-to-section is the one item with a sub-list, so it is rendered inline
  // by each shell rather than flattened into `items`.
  const moveToggle = (
    <button
      type="button"
      role={isPhone ? undefined : 'menuitem'}
      className={isPhone ? 'app-sheet__row' : 'strip-pop__item'}
      aria-expanded={moveOpen}
      disabled={busy || moveTargets.length === 0}
      onClick={() => setMoveOpen((v) => !v)}
    >
      <span className={isPhone ? 'app-sheet__row-label' : undefined}>Move to section</span>{' '}
      {moveOpen ? <ChevronDown /> : <ChevronRight />}
    </button>
  );
  const moveList = moveOpen
    ? moveTargets.map((s) => (
        <button
          key={s.key}
          type="button"
          role={isPhone ? undefined : 'menuitem'}
          className={isPhone ? 'app-sheet__row app-sheet__row--sub' : 'strip-pop__item strip-pop__item--sub'}
          disabled={busy}
          onClick={run(() => onMoveToSection(s.key))}
        >
          <span className={isPhone ? 'app-sheet__row-label' : undefined}>{s.name || s.key}</span>
        </button>
      ))
    : null;

  if (isPhone) {
    return (
      <Sheet title={slide.title || 'Slide'} onClose={onClose}>
        <div ref={rootRef}>
          {/* Same order as the popover, Move-to-section included: the two
              shells are one menu, and a verb that moves position between them
              is a verb people have to re-find. */}
          {itemsBeforeMove.map((it) => (
            <SheetItem key={it.key} item={it} busy={busy} />
          ))}
          {moveToggle}
          {moveList}
          {itemsAfterMove.map((it) => (
            <SheetItem key={it.key} item={it} busy={busy} />
          ))}
        </div>
      </Sheet>
    );
  }

  return (
    <AnchoredPop anchor={anchor} popRef={rootRef} className="strip-pop strip-pop--menu" role="menu">
      {itemsBeforeMove.map((it) => (
        <button
          key={it.key}
          type="button"
          role="menuitem"
          className="strip-pop__item"
          disabled={busy || it.disabled}
          title={it.title}
          onClick={it.onSelect}
        >
          {it.label}
        </button>
      ))}
      {moveToggle}
      {moveList}
      {itemsAfterMove.map((it) => (
        <button
          key={it.key}
          type="button"
          role="menuitem"
          className={'strip-pop__item' + (it.danger ? ' strip-pop__item--danger' : '')}
          disabled={busy || it.disabled}
          title={it.title}
          onClick={it.onSelect}
        >
          {it.label}
        </button>
      ))}
    </AnchoredPop>
  );
}
